import axiosInstance from './axiosInstance';

export type GmailInviteBatchStatus =
  | 'collecting' | 'ready' | 'sending' | 'completed' | 'stopped' | 'failed' | 'discarded' | 'interrupted';

export type InviteRole = 'reader' | 'writer';

/** Where invites go: one of the user's projects with an access level, or (projectId null) 8080.AI itself. */
export interface InviteTarget {
  projectId: string | null;
  role: InviteRole;
}

export interface GmailInviteBatch {
  id: string;
  status: GmailInviteBatchStatus;
  /** ``review``: the user picks each person (Invite people). ``send``: Invite all, from Refer & earn. */
  mode: 'send' | 'review';
  dry_run: boolean;
  /** True while the contact list is held (one hour) for the user to choose from. */
  choosing: boolean;
  sender_email: string | null;
  project_id: string | null;
  role: InviteRole | null;
  contacts_read: number;
  /** Everyone the user could invite (not opted out). */
  contacts_found: number;
  invite_count: number;
  sent_count: number;
  built_count: number;
  failed_count: number;
  error: string | null;
  created_at: string | null;
  completed_at: string | null;
}

export interface InvitePerson {
  email: string;
  name: string | null;
  /** ``contact`` = saved contacts, ``other_contact`` = people the user emailed or replied to. */
  source: 'contact' | 'other_contact' | null;
  status: string;
  /** Already has an 8080.AI account: can be invited to a project, not to 8080.AI. */
  member: boolean;
  /** This user already invited them to 8080.AI. */
  invited: boolean;
}

export interface InvitePeoplePage {
  total: number;
  page: number;
  page_size: number;
  people: InvitePerson[];
  /** The addresses "invite the first N" ticks, in order (up to 200). */
  first: string[];
  choosing: boolean;
}

export interface InviteProject {
  id: string;
  title: string;
  /** Room left on the owner's plan for members and pending invitations; null = unlimited. */
  seats_left: number | null;
  updated_at: string | null;
}

export interface GmailInviteStatus {
  available: boolean;
  reason: string | null;
  dry_run: boolean;
  max_invites: number;
  batch_sizes: number[];
  default_batch_size: number;
  remaining_today: number;
  /** Signs in with Google or has a Gmail address: Refer & earn offers Invite all. */
  google_account: boolean;
  /** Invite all's limit per batch and per 24 hours, and what is left of it now. */
  invite_all_max: number;
  invite_all_remaining: number;
  /** Keep inviting every day: null until it was first turned on. */
  daily?: GmailInviteDaily | null;
  /** The one-time Invite all offer is still to show (the landing page shows it after sign-in). */
  offer?: boolean;
  latest_batch: GmailInviteBatch | null;
}

/** A user's Invite all offer, for admins; every value counts from its last reset. */
export interface GmailInviteOfferAdmin {
  shown_at: string | null;
  credited_at: string | null;
  reset_at: string | null;
  invite_all_count: number;
  /** Contacts read and kept when they allowed contacts at Google sign-in (not counted from a reset). */
  contacts_saved: number;
  /** The popup shows at their next visit to the landing page. */
  shows_next: boolean;
}

/** One press of Invite all: started on the access allowed at sign-in, or ``connect`` to go to Google instead. */
export interface GmailInviteAllStart {
  connect: boolean;
  batch_id: string | null;
}

export interface GmailInviteDaily {
  enabled: boolean;
  sender_email: string;
  next_run_at: string | null;
  last_run_at: string | null;
  /** Why daily invites turned themselves off (Google access was removed). */
  error: string | null;
}

export interface GmailInvitePreview {
  from: string;
  /** Who it comes from, as Gmail shows the sender. */
  sender_name?: string | null;
  sender_email?: string;
  to: string | null;
  to_name?: string | null;
  subject: string;
  /** The exact HTML the person gets (a preview has no open-tracking image). */
  html: string;
  text: string;
}

export type GmailInviteSendResult = { started: true } | { reconnect_url: string };

export interface GmailInviteFunnelSteps {
  sent: number;
  opened: number;
  clicked: number;
  registered: number;
  paid: number;
}

export interface GmailInviteAnalytics {
  start_at: string;
  end_at: string;
  include_dry_runs: boolean;
  totals: GmailInviteFunnelSteps & {
    batches: number;
    inviters: number;
    contacts_found: number;
    failed: number;
    unsubscribed: number;
  };
  daily: Array<GmailInviteFunnelSteps & { date: string }>;
  batches: Array<GmailInviteFunnelSteps & {
    id: string;
    created_at: string;
    inviter_id: string;
    inviter_name: string | null;
    status: GmailInviteBatchStatus;
    mode: 'send' | 'review';
    dry_run: boolean;
    contacts_found: number;
    failed: number;
  }>;
}

const BASE = '/api/v1/gmail-invites';

export const BUSY_STATUSES: ReadonlySet<GmailInviteBatchStatus> = new Set<GmailInviteBatchStatus>(['collecting', 'sending']);
export const INVITE_TOKEN_KEY = 'gmail_invite_token';

const targetParams = (target: InviteTarget) => (
  target.projectId ? { project_id: target.projectId, role: target.role } : {}
);

interface PeopleQuery {
  q: string;
  page: number;
  pageSize: number;
  general: boolean;
}

export const gmailInviteService = {
  async status(): Promise<GmailInviteStatus> {
    return (await axiosInstance.get<GmailInviteStatus>(`${BASE}/status`)).data;
  },

  async projects(): Promise<InviteProject[]> {
    return (await axiosInstance.get<{ projects: InviteProject[] }>(`${BASE}/projects`)).data.projects;
  },

  /**
   * Google's consent page; ``inviteAll`` invites everyone found (Refer & earn) instead of letting the user choose, and
   * ``daily`` also keeps doing it every day (Google is asked for lasting access).
   */
  async connect(returnTo: string, showConsent = false, inviteAll = false, daily = false): Promise<string> {
    const response = await axiosInstance.post<{ authorization_url: string }>(
      `${BASE}/connect`, { return_to: returnTo, show_consent: showConsent, invite_all: inviteAll, daily },
    );

    return response.data.authorization_url;
  },

  /** Turn daily invites off: the stored Google access is deleted and today's batch stops. */
  async inviteAllNow(daily: boolean): Promise<GmailInviteAllStart> {
    return (await axiosInstance.post<GmailInviteAllStart>(`${BASE}/invite-all`, { daily })).data;
  },

  /** The one-time Invite all offer was shown (it never shows again), or cancelled. */
  async recordOffer(event: 'shown' | 'dismissed'): Promise<void> {
    await axiosInstance.post(`${BASE}/offer`, { event });
  },

  async adminOffer(userId: string): Promise<GmailInviteOfferAdmin> {
    const url = `/api/v1/admin/gmail-invites/users/${encodeURIComponent(userId)}/offer`;

    return (await axiosInstance.get<GmailInviteOfferAdmin>(url)).data;
  },

  /** System admins: the offer shows again, and the user's next Invite all can earn the bonus again. */
  async resetAdminOffer(userId: string): Promise<GmailInviteOfferAdmin> {
    const url = `/api/v1/admin/gmail-invites/users/${encodeURIComponent(userId)}/offer/reset`;

    return (await axiosInstance.post<GmailInviteOfferAdmin>(url)).data;
  },

  async stopDaily(): Promise<GmailInviteDaily | null> {
    return (await axiosInstance.post<{ daily: GmailInviteDaily | null }>(`${BASE}/daily/stop`)).data.daily;
  },

  async batch(batchId: string): Promise<GmailInviteBatch> {
    return (await axiosInstance.get<GmailInviteBatch>(`${BASE}/batches/${batchId}`)).data;
  },

  async people(batchId: string, query: PeopleQuery): Promise<InvitePeoplePage> {
    const params = { q: query.q, page: query.page, page_size: query.pageSize, general: query.general };

    return (await axiosInstance.get<InvitePeoplePage>(`${BASE}/batches/${batchId}/people`, { params })).data;
  },

  async samplePreview(target: InviteTarget): Promise<GmailInvitePreview> {
    return (await axiosInstance.get<GmailInvitePreview>(`${BASE}/preview`, { params: targetParams(target) })).data;
  },

  async preview(batchId: string, email: string | null, target: InviteTarget): Promise<GmailInvitePreview> {
    const params = { ...targetParams(target), ...(email ? { email } : {}) };

    return (await axiosInstance.get<GmailInvitePreview>(`${BASE}/batches/${batchId}/preview`, { params })).data;
  },

  async send(
    batchId: string, emails: string[], target: InviteTarget, returnTo: string,
  ): Promise<GmailInviteSendResult> {
    const response = await axiosInstance.post<GmailInviteSendResult>(`${BASE}/batches/${batchId}/send`, {
      emails,
      return_to: returnTo,
      project_id: target.projectId,
      role: target.role,
    });

    return response.data;
  },

  async discard(batchId: string): Promise<void> {
    await axiosInstance.post(`${BASE}/batches/${batchId}/discard`);
  },

  async claim(token: string): Promise<boolean> {
    return (await axiosInstance.post<{ counted: boolean }>(`${BASE}/claim`, { token })).data.counted;
  },

  /** An invite's link opened the referral page: count the click (the link goes straight to the site). */
  async reportClick(token: string): Promise<void> {
    await axiosInstance.post(`${BASE}/t/${encodeURIComponent(token)}/click`);
  },

  async analytics(days: number, includeDryRuns: boolean): Promise<GmailInviteAnalytics> {
    const response = await axiosInstance.get<GmailInviteAnalytics>('/api/v1/admin/gmail-invites/analytics', {
      params: { days, include_dry_runs: includeDryRuns },
    });

    return response.data;
  },
};
